import RecentlyViewed20 from '@carbon/icons-react/es/recently-viewed/20';
import { Dropdown } from 'antd';
import React, { FC, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { UserType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { Form } from 'components/atoms/form';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { logger } from 'services';
import { useDocuments } from '../../document-store';
import { useRevisionsToggle } from './revisions-toggle';

const StyledButton = styled(Button)`
  margin: 0 var(--spacing-03);
`;

const HideChangesToolbar = createGlobalStyle<{ isVersionsActive: boolean }>`
  .ck .ck-balloon-panel {
  display: none;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const CreateRevisionModal: FC<{
  isModalActive: boolean;
  setModalActive: (param: boolean) => void;
}> = ({ isModalActive, setModalActive }) => {
  const [state] = useDocuments();
  const translate = useTranslate();
  const [form] = Form.useForm();
  const versionPlaceholder = translate('document.version.placeholder');

  const onCancel = () => setModalActive(false);
  const saveRevisions = async (versionName: string) => {
    try {
      const revisionTracker = state.editor.plugins.get('RevisionTracker');

      await revisionTracker.saveRevision({
        name: versionName,
      });

      toastService({
        title: translate('document.save.version.success.title'),
        message: translate('document.save.version.success.message', {
          versionName,
        }),
      }).success();
      setModalActive(false);
      form.resetFields();
    } catch (error) {
      toastService({
        title: translate('document.save.version.failure.title'),
        message: translate('document.save.version.failure.message'),
      }).error();

      logger.error('Failed to create a version', { error });
    }
  };

  return (
    <Modal
      closable={false}
      visible={isModalActive}
      destroyOnClose
      title={translate('document.name.version.create.modal.title')}
      width={520}
      footer={
        <ButtonWrapper>
          <div>
            <Button onClick={onCancel} type="ghost">
              {translate('cancel')}
            </Button>
            <Button onClick={form.submit} type="primary">
              {translate('create')}
            </Button>
          </div>
        </ButtonWrapper>
      }
    >
      <Form
        form={form}
        onFinish={(values) => {
          const { versionName } = values as { versionName: string };

          saveRevisions(versionName);
        }}
        colon={false}
        layout="vertical"
      >
        <Form.Item
          required
          rules={[
            {
              required: true,
              message: translate('input.required'),
            },
          ]}
          name="versionName"
          label={translate('document.name.version.input.title')}
        >
          <Input
            autoComplete="off"
            autoFocus
            placeholder={versionPlaceholder}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const RevisionHistoryTrigger: FC = () => {
  const translate = useTranslate();
  const { user } = useUser();
  const [state] = useDocuments();
  const { showRevisionEditor, isRevisionsActive } = useRevisionsToggle();
  const [isRevisionsLoading, setRevisionsLoading] = useState(false);
  const [isModalActive, setModalActive] = useState(false);

  const openModal = () => setModalActive(true);

  const openRevisionEditor = async () => {
    try {
      setRevisionsLoading(true);
      await state.editor?.plugins?.get('RevisionTracker')?.update();
      await state.editor?.config
        ?.get('revisionHistory')
        ?.showRevisionViewerCallback();
      showRevisionEditor();
    } catch {
      toastService({
        title: translate('document.open.version.failure.title'),
        message: translate('document.open.version.failure.message'),
      }).error();
    }
    setRevisionsLoading(false);
  };

  return (
    <>
      <CreateRevisionModal
        isModalActive={isModalActive}
        setModalActive={setModalActive}
      />
      {user.userType !== UserType.External ? (
        <Dropdown
          disabled={!state.editor}
          overlay={
            <Menu>
              <Menu.Item onClick={openModal}>
                {translate('document.save.revision')}
              </Menu.Item>
              <Menu.Item
                disabled={isRevisionsActive || isRevisionsLoading}
                onClick={() => {
                  if (!isRevisionsActive) {
                    openRevisionEditor();
                  }
                }}
              >
                {translate('document.open.revision.history')}
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
          placement="bottomLeft"
        >
          <StyledButton
            size="small"
            type="text"
            loading={isRevisionsLoading}
            icon={
              <IconWrapper>
                <RecentlyViewed20 />
              </IconWrapper>
            }
          />
        </Dropdown>
      ) : null}
      <HideChangesToolbar isVersionsActive={isRevisionsActive} />
    </>
  );
};
